import { useState, useEffect } from "react";
import { usePageSEO } from "../hooks/usePageSEO";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { submitLead, track } from "../lib/analytics";
import { BACKEND_URL } from "../lib/apiBase";
import DarkPartnerAutocomplete from "../components/DarkPartnerAutocomplete";
import {
  ArrowRight, ChevronDown, ChevronLeft, CheckCircle2, ShieldCheck, Brain,
  Clock3, Eye, Scale, Landmark, CalendarDays, Users, Sparkles, XCircle,
  ListChecks, Cpu, KeyRound,
} from "lucide-react";
import log from "../lib/log";

const API = `${BACKEND_URL}/api`;
const LOGO_URL = "/Logo-transparent.png";
const AUTUMN_SALE = Date.now() < new Date("2026-10-31T23:59:59+01:00").getTime();

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString("de-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
      time: d.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" }) + " Uhr",
    };
  } catch { return null; }
};

const scrollToForm = () => {
  document.getElementById("webinar-anmeldung")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* ── kleine Bausteine ─────────────────────────────────────────── */
const Kicker = ({ children }) => (
  <div className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.25em] text-violet-400 mb-4">{children}</div>
);

const CTA = ({ label, testid, ghost }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => { track("webinar_lp_cta_click", { cta: testid }); scrollToForm(); }}
    className={
      ghost
        ? "inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white/90 font-semibold text-sm hover:bg-white/10 transition-colors"
        : "inline-flex items-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-violet-600 to-indigo-500 text-white font-bold text-sm sm:text-base shadow-lg shadow-violet-600/30 hover:shadow-violet-600/50 hover:-translate-y-0.5 transition-all"
    }
  >
    {label} <ArrowRight className="h-4 w-4" />
  </button>
);

const Section = ({ children, className = "", id, testid }) => (
  <section id={id} data-testid={testid} className={`relative px-5 sm:px-8 ${className}`}>
    <div className="max-w-4xl mx-auto">{children}</div>
  </section>
);

function Faq({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-2xl bg-white/[0.03]">
      <button
        type="button"
        data-testid={`faq-${idx}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="font-semibold text-sm sm:text-base text-white">{q}</span>
        <ChevronDown className={`h-4 w-4 text-violet-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 text-sm text-slate-300 leading-relaxed">{a}</div>}
    </div>
  );
}

/* ── Seite ────────────────────────────────────────────────────── */
export default function InsiderReport() {
  const { slug: rawSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isDanke = location.pathname === "/ai/danke";
  const partnerSlug = (rawSlug || "").trim().toLowerCase() || null;

  usePageSEO({
    title: "Kostenloses Live-Webinar: Dein Kapital soll arbeiten – nicht du · Alphalane",
    description: "Mehr aus deinem Kapital machen, ohne selbst zum Trader zu werden: Im kostenlosen Live-Webinar zeigen wir transparent, wie automatisiertes Trading mit KI, klaren Regeln und Risikomanagement funktioniert. Ohne Verkaufsdruck.",
    canonical: "https://alphalane-tradingsystems.com/ai",
  });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [referrerName, setReferrerName] = useState("");
  const [partnerId, setPartnerId] = useState(null);
  const [partnerSlugResolved, setPartnerSlugResolved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [resolvedPartner, setResolvedPartner] = useState(null);
  const [webinar, setWebinar] = useState(null);
  const [totalReturn, setTotalReturn] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (partnerSlug) {
      axios.get(`${API}/partners/by-slug/${encodeURIComponent(partnerSlug)}`)
        .then((r) => {
          if (!cancelled && r.data?.id) {
            setResolvedPartner(r.data);
            setPartnerId(r.data.id);
            setPartnerSlugResolved(r.data.slug);
            setReferrerName(r.data.name);
          }
        })
        .catch((e) => log.debug("[webinar-lp] slug lookup fallback:", e?.response?.status || e));
    }
    axios.get(`${API}/webinars/next`).then((r) => { if (!cancelled) setWebinar(r.data?.webinar || null); }).catch(() => {});
    axios.get(`${API}/track-record`).then((r) => {
      if (!cancelled && Array.isArray(r.data) && r.data.length) {
        setTotalReturn(r.data[r.data.length - 1]?.cumulative ?? null);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [partnerSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (!name || !name.trim()) { setErr("Bitte gib deinen Namen an — Pflichtfeld."); return; }
    if (!resolvedPartner && (!referrerName || !referrerName.trim())) {
      setErr("Bitte gib deinen Empfehlungsgeber an — Pflichtfeld (oder «keiner»).");
      return;
    }
    setErr("");
    setLoading(true);
    try {
      const res = await submitLead(email, name, {
        source: resolvedPartner ? "insider_landing_partner" : "insider_landing",
        phone: phone ? phone.trim() : null,
        partner_id: partnerId,
        partner_slug: partnerSlugResolved,
        referrer_name: referrerName ? referrerName.trim() : (resolvedPartner ? resolvedPartner.name : null),
      });
      if (res) {
        track("insider_landing_submit", { partner_slug: partnerSlugResolved || null });
        setDone(true);
        navigate("/ai/danke");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setErr("Etwas ging schief. Bitte probier es nochmal oder schreib direkt an Pascal.");
      }
    } catch {
      setErr("Etwas ging schief. Bitte probier es nochmal.");
    } finally {
      setLoading(false);
    }
  };

  const wd = fmtDate(webinar?.scheduled_at);
  const total = typeof totalReturn === "number" ? `${totalReturn >= 0 ? "+" : ""}${totalReturn.toFixed(2).replace(".", ",")}%` : null;

  return (
    <div className="min-h-screen text-white overflow-x-hidden" data-testid="insider-report-page"
      style={{ background: "linear-gradient(160deg, #0B0820 0%, #131033 45%, #0B0820 100%)" }}>

      {/* Top bar */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Alphalane" className="h-8 w-auto" />
            <span className="hidden sm:inline font-display font-bold text-white">Alphalane <span className="text-white/50 font-normal text-sm">Trading Systems</span></span>
          </Link>
          <Link to="/" data-testid="insider-back-home" className="text-xs sm:text-sm text-white/60 hover:text-white inline-flex items-center gap-1">
            <ChevronLeft className="h-4 w-4" /> Startseite
          </Link>
        </div>
      </header>

      {(done || isDanke) ? (
        /* ── Confirmation ─────────────────────────────────────── */
        <Section className="py-20 sm:py-28" testid="webinar-confirmation">
          <div className="max-w-xl mx-auto text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center mb-6">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl">Dein Platz ist reserviert.</h1>
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-left space-y-3 text-sm text-slate-300">
              <div className="flex items-center gap-3"><CalendarDays className="h-4 w-4 text-violet-400 flex-shrink-0" /> {wd ? `${wd.date} · ${wd.time}` : "Datum & Uhrzeit erhältst du per E-Mail"}</div>
              <div className="flex items-center gap-3"><Users className="h-4 w-4 text-violet-400 flex-shrink-0" /> Live &amp; online — den Zugangs-Link senden wir dir rechtzeitig per E-Mail</div>
              <div className="flex items-center gap-3"><Sparkles className="h-4 w-4 text-violet-400 flex-shrink-0" /> Dein Bonus: der Alphalane Insider-Report (PDF) ist unterwegs in dein Postfach</div>
            </div>
            <div className="mt-6 rounded-2xl border border-violet-400/25 bg-violet-500/10 p-6 text-left" data-testid="confirmation-next-steps">
              <div className="text-[11px] uppercase tracking-widest font-bold text-violet-300 mb-3">Deine nächsten Schritte</div>
              <ol className="space-y-2.5 text-sm text-slate-200">
                <li className="flex items-start gap-3"><span className="font-display font-bold text-violet-400">1.</span><span>Prüfe dein Postfach: die Bestätigung + der Insider-Report kommen in wenigen Minuten (auch im Spam-Ordner nachsehen).</span></li>
                <li className="flex items-start gap-3"><span className="font-display font-bold text-violet-400">2.</span><span>Der Zugangs-Link zum Webinar kommt <strong className="text-white">per E-Mail</strong> — rechtzeitig vor dem Termin, inkl. Erinnerung.</span></li>
                <li className="flex items-start gap-3"><span className="font-display font-bold text-violet-400">3.</span><span>Blocke dir den Termin im Kalender: 60 Minuten Ruhe — und deine kritischsten Fragen.</span></li>
              </ol>
            </div>
            <Link to="/" data-testid="confirmation-home-link"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white/90 font-semibold text-sm hover:bg-white/10 transition-colors">
              Zur Startseite <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Section>
      ) : (
      <>
        {/* ── 1. HERO ──────────────────────────────────────────── */}
        <Section className="pt-16 sm:pt-24 pb-14" testid="lp-hero">
          <div aria-hidden className="pointer-events-none absolute -top-24 left-1/4 h-96 w-96 rounded-full blur-3xl"
            style={{ background: "radial-gradient(closest-side, rgba(139,92,246,0.35), transparent 70%)" }} />
          <Kicker>Kostenloses Live-Webinar</Kicker>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
            Dein Kapital soll arbeiten.<br />
            <span className="bg-gradient-to-r from-violet-400 to-indigo-300 bg-clip-text text-transparent">Nicht du.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
            Für Menschen, die mehr aus ihrem Geld machen wollen — ohne selbst zum Trader zu werden.
            Im kostenlosen Live-Webinar zeigen wir dir, wie automatisiertes Trading mit KI, klaren Regeln
            und professionellem Risikomanagement für dich arbeitet.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3 text-xs sm:text-sm" data-testid="hero-webinar-meta">
            {[wd ? wd.date : "Nächster Termin folgt", wd ? wd.time : null, "Live · Online", "Kostenlos"].filter(Boolean).map((t) => (
              <span key={t} className="px-3.5 py-1.5 rounded-full border border-white/15 bg-white/[0.05] text-slate-200">{t}</span>
            ))}
          </div>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <CTA label="Kostenlos zum Webinar anmelden" testid="hero-cta" />
            <button type="button" data-testid="hero-cta-secondary"
              onClick={() => document.getElementById("alphalane-erklaert")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white/90 font-semibold text-sm hover:bg-white/10 transition-colors">
              So funktioniert Alphalane
            </button>
          </div>
        </Section>

        {/* ── 2. „Kommt dir das bekannt vor?" ─────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-recognition">
          <Kicker>Kommt dir das bekannt vor?</Kicker>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              "Mein Geld liegt auf dem Konto — und verliert Jahr für Jahr an Kaufkraft.",
              "Ich würde gern mehr aus meinem Kapital machen. Aber mir fehlt schlicht die Zeit.",
              "ETF und Sparkonto sind okay — aber irgendwie frage ich mich: Geht da nicht mehr?",
              "Ich interessiere mich für Märkte — aber ich weiss nicht, wem ich vertrauen kann.",
            ].map((t) => (
              <div key={t} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-slate-200 text-sm sm:text-base leading-relaxed">„{t}"</div>
            ))}
          </div>
          <p className="mt-10 font-display font-bold text-2xl sm:text-3xl leading-snug">
            Vielleicht fehlt dir nicht das Interesse.<br />
            <span className="text-violet-300">Vielleicht fehlt dir ein System, das für dich arbeitet.</span>
          </p>
        </Section>

        {/* ── 3. Problem / Mensch ─────────────────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-problem">
          <Kicker>Das eigentliche Problem</Kicker>
          <div className="text-lg sm:text-xl text-slate-200 leading-relaxed space-y-5 max-w-2xl">
            <p>Du verdienst gut.<br />Du interessierst dich für Märkte und Vermögensaufbau.<br />Du weisst, dass dein Kapital mehr könnte.</p>
            <p>Aber zwischen Job, Familie und Alltag bleibt keine Zeit, selbst zum Trader zu werden —
              und ehrlich gesagt: Das willst du auch gar nicht.</p>
            <p>Also bleibt das Kapital liegen. Nicht, weil dir das Thema egal wäre —
              sondern weil dir niemand einen strukturierten, transparenten Weg zeigt.</p>
            <p className="font-display font-bold text-white text-2xl sm:text-3xl pt-2">
              Das Problem ist nicht dein Wissen.<br />
              <span className="text-violet-300">Es ist die fehlende Zeit — und die fehlende Struktur.</span>
            </p>
          </div>
        </Section>

        {/* ── 4. AHA-Moment ───────────────────────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-aha">
          <h2 className="font-display font-bold text-3xl sm:text-4xl">Selbst traden? Dafür bräuchtest du Zeit.<br /><span className="text-violet-300">Und Nerven.</span></h2>
          <p className="mt-5 text-slate-300 text-base sm:text-lg max-w-2xl">
            Wer selbst tradet, braucht Erfahrung, Disziplin — und muss täglich präsent sein.
            Und selbst erfahrene Trader kämpfen mit dem grössten Gegner: <strong className="text-white">den eigenen Emotionen.</strong>
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {["ANGST", "GIER", "FOMO", "ZWEIFEL", "ZEITDRUCK", "STRESS"].map((t) => (
              <span key={t} className="px-4 py-2 rounded-full border border-rose-400/30 bg-rose-500/10 text-rose-300 text-xs font-bold tracking-widest">{t}</span>
            ))}
          </div>
          <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8 max-w-2xl">
            <p className="text-lg sm:text-xl text-slate-200 leading-relaxed">
              Ein System hat keine Angst.<br />Ein System braucht keinen Feierabend.<br />Ein System arbeitet — während du dein Leben lebst.
            </p>
            <p className="mt-4 font-display font-bold text-white text-xl sm:text-2xl">Aber: Ein System muss Regeln haben.<br />Und du musst es verstehen können.</p>
          </div>
          <div className="mt-8"><CTA label="Kostenloses Webinar ansehen" testid="aha-cta" ghost /></div>
        </Section>

        {/* ── 5. Alphalane als Lösung ─────────────────────────── */}
        <Section id="alphalane-erklaert" className="py-16 sm:py-20 border-t border-white/5" testid="lp-solution">
          <Kicker>Alphalane Trading Systems</Kicker>
          <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">
            Selbstlernend wie ein Mensch.<br />
            <span className="bg-gradient-to-r from-violet-400 to-indigo-300 bg-clip-text text-transparent">Disziplinfest wie ein Bot.</span>
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {[
              [Brain, "Analysiert Märkte & erkennt potenzielle Setups"],
              [Scale, "Bewertet Risiken, bevor gehandelt wird"],
              [Clock3, "Arbeitet 24/5 — auch wenn du schläfst"],
              [Eye, "Analysiert zahlreiche Märkte parallel — emotionsfrei"],
            ].map(([Icon, t]) => (
              <div key={t} className="flex items-start gap-3.5 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                <Icon className="h-5 w-5 text-violet-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm sm:text-base text-slate-200">{t}</span>
              </div>
            ))}
          </div>
          {/* Pipeline */}
          <div className="mt-10 flex flex-col sm:flex-row items-stretch gap-2" data-testid="lp-pipeline">
            {["SIGNAL ENGINE", "RISK ENGINE", "EXECUTION ENGINE", "MONITORING"].map((t, i) => (
              <div key={t} className="flex items-center gap-2 flex-1">
                <div className="flex-1 text-center rounded-xl border border-violet-400/30 bg-violet-500/10 px-3 py-3.5 text-[11px] sm:text-xs font-bold tracking-widest text-violet-200">{t}</div>
                {i < 3 && <ArrowRight className="h-4 w-4 text-violet-400 hidden sm:block flex-shrink-0" />}
              </div>
            ))}
          </div>
        </Section>

        {/* ── 5b. Was du bekommst ─────────────────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-values">
          <Kicker>Was du bekommst</Kicker>
          <h2 className="font-display font-bold text-3xl sm:text-4xl">Fünf Dinge, die dir<br /><span className="text-violet-300">kein Sparkonto gibt.</span></h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              [Clock3, "Zeit", "Das System beobachtet die Märkte — nicht du. Dein Alltag bleibt dein Alltag."],
              [ListChecks, "Struktur", "Klare Regeln statt Bauchgefühl. Jede Entscheidung folgt einem definierten Prozess."],
              [Cpu, "Automatisierung", "Analyse, Ausführung, Überwachung — 24/5 automatisiert, ohne dass du eingreifen musst."],
              [KeyRound, "Kontrolle", "Das Konto läuft auf deinen Namen. Du behältst jederzeit den Zugriff auf dein Kapital."],
              [Eye, "Transparenz", "Live-Dashboard und öffentlicher Track Record — inklusive Verlustmonaten."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="rounded-2xl border border-violet-400/25 bg-violet-500/[0.07] p-6" data-testid={`lp-value-${t.toLowerCase()}`}>
                <Icon className="h-6 w-6 text-violet-300 mb-3" />
                <div className="font-display font-bold text-white text-lg">{t}</div>
                <p className="mt-1.5 text-sm text-slate-300 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* ── 6. Warum nicht einfach ein Bot? ─────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-vs-bot">
          <h2 className="font-display font-bold text-3xl sm:text-4xl">Aber ist Alphalane nicht einfach ein Trading-Bot?</h2>
          <p className="mt-4 text-slate-300 text-lg">Nein — und genau hier liegt der Unterschied.</p>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {[
              ["Mensch", ["lernfähig", "emotional", "begrenzte Aufmerksamkeit", "wenige Märkte gleichzeitig"], false],
              ["Standard-Bot", ["emotionsfrei", "regelbasiert", "statisch", "keine kontinuierliche Anpassung"], false],
              ["Alphalane", ["selbstlernend", "emotionsfrei & regelbasiert", "News-aware · 1'000+ Märkte", "24/5 · kontinuierliche Anpassung"], true],
            ].map(([title, items, highlight]) => (
              <div key={title} className={`rounded-2xl p-6 border ${highlight ? "border-violet-400/50 bg-violet-500/10 shadow-lg shadow-violet-600/20" : "border-white/10 bg-white/[0.04]"}`}>
                <div className={`font-display font-bold mb-4 ${highlight ? "text-violet-300" : "text-white"}`}>{title}</div>
                <ul className="space-y-2.5 text-sm text-slate-300">
                  {items.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <CheckCircle2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${highlight ? "text-violet-400" : "text-slate-500"}`} /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* ── 7. Was wir NICHT versprechen ────────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-no-promises">
          <h2 className="font-display font-bold text-3xl sm:text-4xl">Was wir dir <span className="text-rose-400">nicht</span> versprechen.</h2>
          <div className="mt-8 space-y-3 max-w-2xl">
            {["Keine garantierten Renditen.", "Keine risikofreien Gewinne.", "Keine Versprechen, dass jeder Monat positiv endet."].map((t) => (
              <div key={t} className="flex items-center gap-3 rounded-xl border border-rose-400/25 bg-rose-500/5 px-5 py-4 text-slate-200">
                <XCircle className="h-5 w-5 text-rose-400 flex-shrink-0" /> {t}
              </div>
            ))}
          </div>
          <p className="mt-6 text-slate-300 text-lg">Denn genau das unterscheidet einen seriösen Ansatz von Marketing.</p>
          <div className="mt-8 rounded-2xl border border-emerald-400/25 bg-emerald-500/5 p-6 max-w-2xl">
            <div className="font-bold text-emerald-300 mb-3">Was wir stattdessen versprechen können:</div>
            <div className="flex flex-wrap gap-2.5">
              {["Transparenz", "Nachvollziehbare Regeln", "Live-Performance", "Risikomanagement", "Ein Konto auf deinen Namen"].map((t) => (
                <span key={t} className="px-3.5 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-xs font-semibold">{t}</span>
              ))}
            </div>
          </div>
        </Section>

        {/* ── 8. Track Record ─────────────────────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-track-record">
          <Kicker>Track Record</Kicker>
          <h2 className="font-display font-bold text-3xl sm:text-4xl">Nicht Backtest.<br /><span className="text-violet-300">Live.</span></h2>
          <p className="mt-4 text-slate-300 text-lg">Echte Performance. Echte Gewinne. Echte Verluste.</p>
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {total && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-7 py-5">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Gesamtrendite seit Mai 2025</div>
                <div className="font-display font-bold text-4xl mt-1 text-emerald-400" data-testid="lp-total-return">{total}</div>
                <div className="text-[11px] text-slate-500 mt-1">inkl. Verlustmonaten — live von MyFxBook</div>
              </div>
            )}
            <a href="/#track-record" data-testid="lp-track-record-link"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white/90 font-semibold text-sm hover:bg-white/10 transition-colors">
              Aktuelle Live-Performance ansehen <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <p className="mt-5 text-xs text-slate-500 max-w-xl">Vergangene Performance ist keine Garantie für zukünftige Ergebnisse.</p>
        </Section>

        {/* ── 8b. Skeptiker ───────────────────────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-skeptic">
          <h2 className="font-display font-bold text-3xl sm:text-4xl">Du bist skeptisch? <span className="text-violet-300">Gut.</span></h2>
          <p className="mt-4 text-slate-300 text-lg">Das solltest du sein — gerade weil du dein Kapital nicht selbst jeden Tag im Blick hast.
            Bevor du irgendjemandem dein Kapital anvertraust, solltest du diese Fragen stellen:</p>
          <div className="mt-7 grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl text-sm sm:text-base text-slate-200">
            {["Wo liegt mein Geld?", "Wer hat Zugriff darauf?", "Gibt es einen echten Track Record?", "Wie wird Risiko kontrolliert?", "Was passiert bei Verlusten?", "Kann ich jederzeit aussteigen?", "Wer steht hinter dem Unternehmen?", "Wie funktioniert das System?"].map((t) => (
              <div key={t} className="flex items-center gap-2.5"><CheckCircle2 className="h-4 w-4 text-violet-400 flex-shrink-0" /> {t}</div>
            ))}
          </div>
          <p className="mt-7 text-slate-300">Genau diese Fragen beantworten wir im Webinar — offen, live und mit der Möglichkeit nachzuhaken.</p>
          <div className="mt-6"><CTA label="Fragen direkt stellen" testid="skeptic-cta" ghost /></div>
        </Section>

        {/* ── 9. Risikomanagement ─────────────────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-risk">
          <h2 className="font-display font-bold text-3xl sm:text-4xl">Was passiert, wenn der Markt gegen dich läuft?</h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {[
              ["Position Sizing", "Kontrollierte Positionsgrössen — kein Alles-oder-Nichts."],
              ["Daily Loss Limit", "Automatische Begrenzung täglicher Verluste."],
              ["Drawdown-Trigger", "Das System kann bei definierten Drawdowns pausieren."],
              ["Korrelation", "Offene Positionen werden nicht isoliert betrachtet."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <div className="flex items-center gap-2.5 font-bold text-white"><ShieldCheck className="h-5 w-5 text-violet-400" /> {t}</div>
                <p className="mt-2 text-sm text-slate-300">{d}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* ── 10. Dein Geld bleibt dein Geld ──────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-your-money">
          <div className="rounded-3xl border border-violet-400/30 bg-gradient-to-br from-violet-600/15 to-indigo-600/10 p-8 sm:p-10">
            <Landmark className="h-8 w-8 text-violet-300 mb-4" />
            <h2 className="font-display font-bold text-3xl sm:text-4xl">Du überweist dein Kapital nicht an uns.</h2>
            <ul className="mt-6 space-y-3 text-slate-200 text-base sm:text-lg">
              <li className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-violet-300 mt-1 flex-shrink-0" /> Das Handelskonto läuft auf deinen Namen.</li>
              <li className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-violet-300 mt-1 flex-shrink-0" /> Du behältst die Kontrolle über dein Konto.</li>
              <li className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-violet-300 mt-1 flex-shrink-0" /> Alphalane benötigt lediglich die vereinbarten Handelsberechtigungen.</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-2.5 text-xs">
              {["Schweizer Unternehmen", "Eigenes Kundenkonto", AUTUMN_SALE ? "Herbst-Sale: 20% statt 40% Performance Fee — nur auf Gewinne (bis 31. Oktober)" : "Performance Fee 40% — nur auf Gewinne", "Kein Abo · keine Startgebühr", "Live-Dashboard"].map((t) => (
                <span key={t} className="px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-slate-200">{t}</span>
              ))}
            </div>
          </div>
        </Section>

        {/* ── 11. Webinar-Agenda ──────────────────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-agenda">
          <Kicker>Das Live-Webinar</Kicker>
          <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">
            Bevor du eine Entscheidung triffst:<br /><span className="text-violet-300">Versteh erst, wie es funktioniert.</span>
          </h2>
          <p className="mt-4 text-slate-300 text-base sm:text-lg max-w-2xl">
            Im kostenlosen Live-Webinar zeigen wir dir, wie du dein Kapital arbeiten lässt,
            ohne selbst zum Trader zu werden — transparent, verständlich und ohne Verkaufsdruck.
          </p>
          <div className="mt-8 space-y-2.5 max-w-2xl">
            {[
              "Warum dein Kapital auf dem Konto real an Wert verliert",
              "Wie automatisiertes Trading funktioniert — verständlich, ohne Fachchinesisch",
              "Was KI dabei wirklich leisten kann — und was nicht",
              "Wie du dein Kapital arbeiten lässt, ohne selbst zum Trader zu werden",
              "Wie Risiko kontrolliert wird — und was in Verlustmonaten passiert",
              "Warum du die Kontrolle über dein Konto nie abgibst",
              "Live-Fragerunde — stell uns deine kritischsten Fragen",
            ].map((t, i) => (
              <div key={t} className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] px-5 py-3.5">
                <span className="font-display font-bold text-violet-400 text-sm w-7">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm sm:text-base text-slate-200">{t}</span>
              </div>
            ))}
          </div>
          <div className="mt-8"><CTA label="Meinen Platz kostenlos sichern" testid="agenda-cta" /></div>
        </Section>

        {/* ── 12. Transformation ──────────────────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-transformation">
          <h2 className="font-display font-bold text-3xl sm:text-4xl">Nicht mehr aufschieben.<br /><span className="text-violet-300">Verstehen — und delegieren.</span></h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-3xl">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="text-[11px] uppercase tracking-widest text-rose-300 font-bold mb-4">Vorher</div>
              <ul className="space-y-2.5 text-sm text-slate-400">
                {["Mein Geld liegt einfach nur herum.", "Ich habe keine Zeit, mich darum zu kümmern.", "Ich weiss nicht, wem ich vertrauen kann.", "Ich verstehe nicht, wie das funktioniert.", "Ich schiebe das Thema seit Jahren vor mir her."].map((t) => <li key={t}>„{t}"</li>)}
              </ul>
            </div>
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-6">
              <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-bold mb-4">Nachher</div>
              <ul className="space-y-2.5 text-sm text-slate-200">
                {["Ich verstehe, wie mein Kapital arbeitet.", "Ich kenne die Risiken — und die Grenzen.", "Ich sehe die Performance live im Dashboard.", "Ich weiss, wer dahintersteht.", "Ich habe delegiert — ohne die Kontrolle abzugeben."].map((t) => <li key={t}>„{t}"</li>)}
              </ul>
            </div>
          </div>
        </Section>

        {/* ── 14. Gründer ─────────────────────────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-founders">
          <Kicker>Transparenz statt Testimonials</Kicker>
          <div className="flex items-center gap-5 mb-6" data-testid="lp-founders-photos">
            <div className="flex -space-x-4">
              <img src="/team-adrian.jpg" alt="Adrian Bienz"
                className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover ring-4 ring-violet-400/30 shadow-lg shadow-violet-600/20" />
              <img src="/team-pascal.jpg" alt="Pascal Schwab"
                className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover ring-4 ring-violet-400/30 shadow-lg shadow-violet-600/20" />
            </div>
            <div className="text-sm text-slate-300">
              <div className="font-bold text-white">Adrian Bienz &amp; Pascal Schwab</div>
              <div>Gründer von Alphalane</div>
            </div>
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl">Hinter der Technologie stehen Menschen.</h2>
          <p className="mt-4 text-slate-300 text-base sm:text-lg max-w-2xl">
            Du sprichst nicht mit einem anonymen Trading-Bot. Du kannst mit den Menschen sprechen,
            die hinter Alphalane stehen: <strong className="text-white">Pascal &amp; Adrian</strong> — deine persönlichen
            Ansprechpartner, vom ersten Gespräch bis zum laufenden Betrieb. Dazu: Live-Track-Record,
            eigenes Dashboard und klare Vertragsbedingungen statt anonymer Versprechen.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <CTA label="Live dabei sein" testid="founders-cta" />
            <Link to="/about" data-testid="founders-about-link"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white/90 font-semibold text-sm hover:bg-white/10 transition-colors">
              Persönliches Kennenlernen <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Section>

        {/* ── 15. FAQ ─────────────────────────────────────────── */}
        <Section className="py-16 sm:py-20 border-t border-white/5" testid="lp-faq">
          <Kicker>Häufige Fragen</Kicker>
          <div className="space-y-3 max-w-2xl">
            {[
              ["Ist Alphalane ein Trading-Bot?", "Nein — ein klassischer Bot folgt statischen Regeln. Alphalane kombiniert regelbasiertes Trading mit selbstlernender KI, News-Awareness und kontinuierlicher Marktanpassung, arbeitet aber genauso emotionsfrei und diszipliniert."],
              ["Wo liegt mein Geld?", "Auf einem Handelskonto bei einem Broker — auf deinen Namen. Du überweist dein Kapital nicht an uns; Alphalane erhält nur die vereinbarten Handelsberechtigungen."],
              ["Kann ich mein Konto selbst einsehen und Geld auszahlen?", "Ja. Du hast jederzeit vollen Zugriff auf dein Konto und dein persönliches Live-Dashboard. Auszahlungen veranlasst du selbst über deinen Broker."],
              ["Welche Risiken gibt es?", "Trading mit gehebelten Finanzinstrumenten kann zu erheblichen Verlusten bis hin zum Totalverlust führen. Es gibt keine garantierten Renditen und auch Verlustmonate. Das Risikomanagement (Position Sizing, Loss Limits, Drawdown-Trigger) begrenzt Risiken — es eliminiert sie nicht."],
              ["Was passiert in einem Verlustmonat?", "Verlustmonate gehören zu jedem echten Track Record — unserer ist live einsehbar, inklusive der negativen Monate. In Verlustmonaten fällt keine Performance Fee an: Sie wird nur auf Gewinne berechnet (High-Watermark-Prinzip)."],
              ["Wie hoch sind die Kosten?", AUTUMN_SALE ? "Herbst-Sale bis 31. Oktober: nur 20% statt 40% Performance Fee — ausschliesslich auf realisierte Gewinne. Kein Abo, keine Startgebühr, keine Fixkosten." : "Performance Fee: 40% — ausschliesslich auf realisierte Gewinne. Kein Abo, keine Startgebühr, keine Fixkosten."],
              ["Gibt es eine Mindestlaufzeit? Kann ich kündigen?", "Die Konditionen inkl. Laufzeit und Kündigungsmöglichkeiten sind im Dienstleistungsvertrag transparent geregelt — und du kannst das Trading auf deinem Konto pausieren. Details klären wir gerne im Webinar oder im persönlichen Gespräch."],
              ["Brauche ich Trading-Erfahrung?", "Nein. Das Onboarding führt dich Schritt für Schritt durch Konto-Eröffnung und Verknüpfung — mit Videos und persönlicher Unterstützung durch Pascal & Adrian."],
              ["Ist das eine Anlageberatung?", "Nein. Alphalane ist eine Technologie-Dienstleistung. Wir geben keine Anlage-, Steuer- oder Rechtsberatung. Die Entscheidung, ob und wie viel Kapital du einsetzt, triffst allein du."],
              ["Wie kann ich das System kennenlernen?", "Im kostenlosen Live-Webinar — dort zeigen wir transparent, wie das System funktioniert, und beantworten deine Fragen live. Danach entscheidest du in Ruhe."],
            ].map(([q, a], i) => <Faq key={q} q={q} a={a} idx={i} />)}
          </div>
        </Section>

        {/* ── 16. Finaler CTA + Formular ──────────────────────── */}
        <Section id="webinar-anmeldung" className="py-16 sm:py-24 border-t border-white/5" testid="lp-final-cta">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display font-bold text-3xl sm:text-5xl leading-tight">Du musst heute keine Entscheidung treffen.</h2>
            <p className="mt-4 text-slate-300 text-base sm:text-lg">Aber du kannst dir 60 Minuten nehmen, um zu verstehen, wie dein Kapital für dich arbeiten könnte — und was realistisch ist.</p>
            <div className="mt-5 font-display font-bold text-violet-300 text-xl tracking-wide">Live. Kostenlos. Transparent.</div>
          </div>

          <form onSubmit={submit} data-testid="insider-form"
            className="mt-10 max-w-lg mx-auto rounded-3xl border border-white/15 bg-white/[0.05] backdrop-blur p-6 sm:p-8 space-y-4">
            {wd && (
              <div className="flex items-center gap-2.5 text-sm text-slate-200 rounded-xl bg-violet-500/10 border border-violet-400/25 px-4 py-3">
                <CalendarDays className="h-4 w-4 text-violet-300 flex-shrink-0" /> {wd.date} · {wd.time}
              </div>
            )}
            <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="insider-name-input"
              placeholder="Dein Name *" className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3.5 text-white placeholder-slate-400 outline-none focus:border-violet-400" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="insider-email-input"
              placeholder="Deine E-Mail *" className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3.5 text-white placeholder-slate-400 outline-none focus:border-violet-400" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="insider-phone-input"
              placeholder="Telefon (optional)" className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3.5 text-white placeholder-slate-400 outline-none focus:border-violet-400" />
            {resolvedPartner ? (
              <div className="text-xs text-slate-300 rounded-xl bg-white/5 border border-white/10 px-4 py-3" data-testid="insider-partner-resolved">
                Du wurdest von <strong className="text-white">{resolvedPartner.name}</strong> zu Alphalane eingeladen
              </div>
            ) : (
              <DarkPartnerAutocomplete
                value={referrerName}
                onChange={(name, pid) => { setReferrerName(name); setPartnerId(pid); }}
                placeholder="Wer hat dich empfohlen? * («keiner» ist ok)"
                required
                testid="insider-referrer-input"
              />
            )}
            {err && <div className="text-sm text-rose-300" data-testid="insider-form-error">{err}</div>}
            <button type="submit" disabled={loading} data-testid="insider-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-violet-600 to-indigo-500 text-white font-bold shadow-lg shadow-violet-600/30 hover:shadow-violet-600/50 transition-all disabled:opacity-60">
              {loading ? "Wird gesendet..." : "Jetzt Webinar-Platz sichern"} <ArrowRight className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-2.5 text-[12px] text-slate-300 rounded-xl bg-emerald-500/10 border border-emerald-400/25 px-4 py-3" data-testid="insider-price-anchor">
              <Sparkles className="h-4 w-4 text-emerald-300 flex-shrink-0" />
              <span>Anmelde-Bonus: der <strong className="text-white">Alphalane Insider-Report</strong> (PDF) — <span className="al-slash font-bold">CHF 49.–</span> <strong className="text-emerald-300">gratis</strong>, sofort per Mail</span>
            </div>
            <p className="text-[11px] text-slate-400 text-center">Keine Verpflichtung. Keine Anlageberatung. Keine Renditeversprechen.</p>
          </form>
        </Section>

        {/* Sticky Mobile CTA */}
        <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden px-4 pb-4 pt-8 pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(11,8,32,0.95) 40%, transparent)" }}>
          <button type="button" data-testid="sticky-mobile-cta" onClick={scrollToForm}
            className="pointer-events-auto w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-gradient-to-r from-violet-600 to-indigo-500 text-white font-bold shadow-xl shadow-violet-600/40">
            Kostenlos zum Webinar anmelden <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </>
      )}

      {/* Footer / Rechtliches */}
      <footer className="border-t border-white/10 px-5 sm:px-8 py-10 pb-28 lg:pb-10">
        <div className="max-w-4xl mx-auto text-[11px] leading-relaxed text-slate-500 space-y-2">
          <p><strong className="text-slate-400">Risikohinweis:</strong> Trading mit gehebelten Finanzinstrumenten kann zu erheblichen Verlusten bis hin zum Totalverlust des eingesetzten Kapitals führen. Vergangene Performance ist keine Garantie für zukünftige Ergebnisse. Die Inhalte stellen keine Anlage-, Steuer- oder Rechtsberatung dar. Investiere nur Kapital, dessen Verlust für dich finanziell tragbar ist.</p>
          <p>© {new Date().getFullYear()} Alphalane Trading Systems · Moonshine Solutions GmbH · Schmitten FR, Schweiz · <Link to="/impressum" className="underline hover:text-slate-300">Impressum</Link> · <Link to="/datenschutz" className="underline hover:text-slate-300">Datenschutz</Link></p>
        </div>
      </footer>
    </div>
  );
}
